import React, { useState } from 'react';
import { ModelConfig } from '../utils/financialEngine';
import { SliderInput } from './SliderInput';
import { Home, Key, ShoppingCart, Percent, DollarSign, FileText, ChevronDown, ChevronUp } from 'lucide-react';

interface InputPanelProps {
  config: ModelConfig;
  onChange: (updatedConfig: ModelConfig) => void;
}

export const InputPanel: React.FC<InputPanelProps> = ({ config, onChange }) => {
  const [openSection, setOpenSection] = useState<'home1' | 'rental' | 'home2' | 'financing' | 'macro' | 'tax'>('home1');

  const toggleSection = (section: 'home1' | 'rental' | 'home2' | 'financing' | 'macro' | 'tax') => {
    setOpenSection(openSection === section ? section : section);
  };

  const updateHome1 = (field: keyof ModelConfig['home1'], val: number) => {
    onChange({
      ...config,
      home1: { ...config.home1, [field]: val }
    });
  };

  const updateRental = (field: keyof ModelConfig['rental'], val: number) => {
    onChange({
      ...config,
      rental: { ...config.rental, [field]: val }
    });
  };

  const updateKeepFinancing = (field: keyof ModelConfig['keepFinancing'], val: any) => {
    onChange({
      ...config,
      keepFinancing: { ...config.keepFinancing, [field]: val }
    });
  };

  const updateMacro = (field: keyof ModelConfig['macro'], val: number) => {
    onChange({
      ...config,
      macro: { ...config.macro, [field]: val }
    });
  };

  const updateSale = (field: keyof ModelConfig['sale'], val: any) => {
    onChange({
      ...config,
      sale: { ...config.sale, [field]: val }
    });
  };

  return (
    <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 p-4 border border-slate-800/80 shadow-xl space-y-3">
      <div className="flex items-center justify-between border-b border-slate-800/60 pb-3">
        <h3 className="font-heading text-base font-bold text-white flex items-center gap-2">
          Interactive Scenario Parameters
        </h3>
        <span className="text-xs text-cyan-400 font-mono">Real-time simulation</span>
      </div>

      {/* Accordion 1: Home #1 Current Details */}
      <div className="border border-slate-800/80 rounded-xl overflow-hidden bg-slate-950/40">
        <button
          onClick={() => toggleSection('home1')}
          className="w-full flex items-center justify-between p-3 text-left font-medium text-xs text-slate-200 hover:bg-slate-900/60 transition-colors"
        >
          <span className="flex items-center gap-2 font-semibold">
            <Home className="w-4 h-4 text-cyan-400" />
            Home #1 Current Property & Loan
          </span>
          {openSection === 'home1' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {openSection === 'home1' && (
          <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5 border-t border-slate-800/60">
            <SliderInput
              label="Home #1 Market Value"
              value={config.home1.currentValue}
              onChange={(v) => updateHome1('currentValue', v)}
              min={100000}
              max={3000000}
              step={10000}
              format="currency"
            />
            <SliderInput
              label="Mortgage Balance"
              value={config.home1.mortgageBalance}
              onChange={(v) => updateHome1('mortgageBalance', v)}
              min={0}
              max={config.home1.currentValue}
              step={5000}
              format="currency"
            />
            <SliderInput
              label="Mortgage Interest Rate"
              value={config.home1.interestRate}
              onChange={(v) => updateHome1('interestRate', v)}
              min={1.0}
              max={12.0}
              step={0.1}
              format="percent"
            />
            <SliderInput
              label="Property Tax Rate"
              value={config.home1.propertyTaxRate}
              onChange={(v) => updateHome1('propertyTaxRate', v)}
              min={0.5}
              max={3.5}
              step={0.05}
              format="percent"
            />
            <SliderInput
              label="Annual Insurance"
              value={config.home1.annualInsurance}
              onChange={(v) => updateHome1('annualInsurance', v)}
              min={500}
              max={10000}
              step={100}
              format="currency"
            />
            <SliderInput
              label="Monthly HOA"
              value={config.home1.monthlyHoa}
              onChange={(v) => updateHome1('monthlyHoa', v)}
              min={0}
              max={2000}
              step={25}
              format="currency"
            />
          </div>
        )}
      </div>

      {/* Accordion 2: Home #1 Rental Income & Expenses */}
      <div className="border border-slate-800/80 rounded-xl overflow-hidden bg-slate-950/40">
        <button
          onClick={() => toggleSection('rental')}
          className="w-full flex items-center justify-between p-3 text-left font-medium text-xs text-slate-200 hover:bg-slate-900/60 transition-colors"
        >
          <span className="flex items-center gap-2 font-semibold">
            <Key className="w-4 h-4 text-emerald-400" />
            Home #1 Rental Performance Inputs
          </span>
          {openSection === 'rental' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {openSection === 'rental' && (
          <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5 border-t border-slate-800/60">
            <SliderInput
              label="Monthly Gross Rent"
              value={config.rental.monthlyGrossRent}
              onChange={(v) => updateRental('monthlyGrossRent', v)}
              min={1000}
              max={15000}
              step={50}
              format="currency"
            />
            <SliderInput
              label="Vacancy Allowance"
              value={config.rental.vacancyRate}
              onChange={(v) => updateRental('vacancyRate', v)}
              min={0}
              max={20}
              step={0.5}
              format="percent"
            />
            <SliderInput
              label="Property Management"
              value={config.rental.propertyManagementRate}
              onChange={(v) => updateRental('propertyManagementRate', v)}
              min={0}
              max={15}
              step={0.5}
              format="percent"
            />
            <SliderInput
              label="Maintenance Reserve"
              value={config.rental.maintenanceReserveRate}
              onChange={(v) => updateRental('maintenanceReserveRate', v)}
              min={0}
              max={15}
              step={0.5}
              format="percent"
            />
          </div>
        )}
      </div>

      {/* Accordion 3: Home #2 Purchase Parameters */}
      <div className="border border-slate-800/80 rounded-xl overflow-hidden bg-slate-950/40">
        <button
          onClick={() => toggleSection('home2')}
          className="w-full flex items-center justify-between p-3 text-left font-medium text-xs text-slate-200 hover:bg-slate-900/60 transition-colors"
        >
          <span className="flex items-center gap-2 font-semibold">
            <ShoppingCart className="w-4 h-4 text-indigo-400" />
            Home #2 Purchase Details
          </span>
          {openSection === 'home2' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {openSection === 'home2' && (
          <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5 border-t border-slate-800/60">
            <SliderInput
              label="Home #2 Purchase Price"
              value={config.home2PurchasePrice}
              onChange={(v) => onChange({ ...config, home2PurchasePrice: v })}
              min={200000}
              max={5000000}
              step={25000}
              format="currency"
            />
            <SliderInput
              label="Target Down Payment"
              value={config.home2DownPaymentPct}
              onChange={(v) => onChange({ ...config, home2DownPaymentPct: v })}
              min={5.0}
              max={50.0}
              step={1.0}
              format="percent"
            />
            <SliderInput
              label="Home #2 Interest Rate"
              value={config.home2InterestRate}
              onChange={(v) => onChange({ ...config, home2InterestRate: v })}
              min={3.0}
              max={12.0}
              step={0.125}
              format="percent"
            />
            <SliderInput
              label="Home #2 Property Tax"
              value={config.home2PropertyTaxRate}
              onChange={(v) => onChange({ ...config, home2PropertyTaxRate: v })}
              min={0.5}
              max={3.5}
              step={0.05}
              format="percent"
            />
          </div>
        )}
      </div>

      {/* Accordion 4: Keep & Rent Down Payment Funding (Cash vs HELOC) */}
      <div className="border border-slate-800/80 rounded-xl overflow-hidden bg-slate-950/40">
        <button
          onClick={() => toggleSection('financing')}
          className="w-full flex items-center justify-between p-3 text-left font-medium text-xs text-slate-200 hover:bg-slate-900/60 transition-colors"
        >
          <span className="flex items-center gap-2 font-semibold">
            <DollarSign className="w-4 h-4 text-violet-400" />
            Keep & Rent Down Payment Funding
          </span>
          {openSection === 'financing' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {openSection === 'financing' && (
          <div className="p-3 space-y-3 border-t border-slate-800/60">
            <div className="flex items-center gap-3">
              <label className="text-xs font-medium text-slate-300">Funding Source:</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => updateKeepFinancing('fundingSource', 'HELOC')}
                  className={`px-3 py-1 rounded-lg text-xs font-medium border transition-all ${
                    config.keepFinancing.fundingSource === 'HELOC'
                      ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400'
                      : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white'
                  }`}
                >
                  HELOC + Savings
                </button>
                <button
                  onClick={() => updateKeepFinancing('fundingSource', 'CASH')}
                  className={`px-3 py-1 rounded-lg text-xs font-medium border transition-all ${
                    config.keepFinancing.fundingSource === 'CASH'
                      ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400'
                      : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white'
                  }`}
                >
                  100% Cash Savings
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <SliderInput
                label="Liquid Cash Savings Used"
                value={config.keepFinancing.cashSavingsAllocated}
                onChange={(v) => updateKeepFinancing('cashSavingsAllocated', v)}
                min={0}
                max={500000}
                step={5000}
                format="currency"
              />
              {config.keepFinancing.fundingSource === 'HELOC' && (
                <SliderInput
                  label="HELOC Interest Rate"
                  value={config.keepFinancing.helocInterestRate}
                  onChange={(v) => updateKeepFinancing('helocInterestRate', v)}
                  min={4.0}
                  max={15.0}
                  step={0.25}
                  format="percent"
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Accordion 5: Macro Appreciation & Market Growth */}
      <div className="border border-slate-800/80 rounded-xl overflow-hidden bg-slate-950/40">
        <button
          onClick={() => toggleSection('macro')}
          className="w-full flex items-center justify-between p-3 text-left font-medium text-xs text-slate-200 hover:bg-slate-900/60 transition-colors"
        >
          <span className="flex items-center gap-2 font-semibold">
            <Percent className="w-4 h-4 text-cyan-400" />
            Macro Growth & Market Projections
          </span>
          {openSection === 'macro' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {openSection === 'macro' && (
          <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5 border-t border-slate-800/60">
            <SliderInput
              label="Home #1 Appreciation Rate"
              value={config.macro.home1AppreciationRate}
              onChange={(v) => updateMacro('home1AppreciationRate', v)}
              min={0}
              max={10}
              step={0.25}
              format="percent"
            />
            <SliderInput
              label="Home #2 Appreciation Rate"
              value={config.macro.home2AppreciationRate}
              onChange={(v) => updateMacro('home2AppreciationRate', v)}
              min={0}
              max={10}
              step={0.25}
              format="percent"
            />
            <SliderInput
              label="Annual Rent Growth"
              value={config.macro.rentGrowthRate}
              onChange={(v) => updateMacro('rentGrowthRate', v)}
              min={0}
              max={8}
              step={0.25}
              format="percent"
            />
            <SliderInput
              label="Liquid Cash Investment Return"
              value={config.macro.investmentReturnRate}
              onChange={(v) => updateMacro('investmentReturnRate', v)}
              min={0}
              max={12}
              step={0.5}
              format="percent"
            />
          </div>
        )}
      </div>

      {/* Accordion 6: Tax & Selling Costs (Section 121) */}
      <div className="border border-slate-800/80 rounded-xl overflow-hidden bg-slate-950/40">
        <button
          onClick={() => toggleSection('tax')}
          className="w-full flex items-center justify-between p-3 text-left font-medium text-xs text-slate-200 hover:bg-slate-900/60 transition-colors"
        >
          <span className="flex items-center gap-2 font-semibold">
            <FileText className="w-4 h-4 text-emerald-400" />
            Tax Bracket & Section 121 Sale Settings
          </span>
          {openSection === 'tax' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {openSection === 'tax' && (
          <div className="p-3 space-y-3 border-t border-slate-800/60">
            <div className="flex items-center gap-3">
              <label className="text-xs font-medium text-slate-300">Filing Status:</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => updateSale('filingStatus', 'MARRIED')}
                  className={`px-3 py-1 rounded-lg text-xs font-medium border transition-all ${
                    config.sale.filingStatus === 'MARRIED'
                      ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400'
                      : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white'
                  }`}
                >
                  Married ($500k Sec 121)
                </button>
                <button
                  onClick={() => updateSale('filingStatus', 'SINGLE')}
                  className={`px-3 py-1 rounded-lg text-xs font-medium border transition-all ${
                    config.sale.filingStatus === 'SINGLE'
                      ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400'
                      : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white'
                  }`}
                >
                  Single ($250k Sec 121)
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <SliderInput
                label="Marginal Income Tax Rate"
                value={config.rental.marginalTaxRate}
                onChange={(v) => updateRental('marginalTaxRate', v)}
                min={10}
                max={37}
                step={1}
                format="percent"
              />
              <SliderInput
                label="Selling Costs & Commissions"
                value={config.sale.sellingCostRate}
                onChange={(v) => updateSale('sellingCostRate', v)}
                min={3.0}
                max={10.0}
                step={0.5}
                format="percent"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
